/**
 * The PrimeVue theme: Aura, retuned to the Tally kit (Dashboard v2 and the list-page restyle,
 * 86bcgb44h). Restyling through the preset rather than per page means every DataTable, Button,
 * Tag, Paginator, Dialog, Drawer, input and menu in the app picks up the look at once.
 *
 * Values mirror the --tally-* tokens in assets/metrics.css (and the canvas Kit artboard). The root
 * font size is 13px (assets/styles.scss), so everything here is in px to stay exact.
 */
import Aura from '@primevue/themes/aura';
import { definePreset } from '@primevue/themes';

const ink = '#14304a';
const nav = '#3d4b5c';
const body = '#6b7a88';
const muted = '#8a97a5';
const tile = '#f7f9fa';
const hairline = '#eef1f4';
const rowRule = '#f2f4f6';
const inputBorder = '#d7e0e5';
const navy = '#08334b';
const navyHover = '#1b5476';
const accent = '#2f6e94';
const tint = '#edf3f5';
const info = '#3d5a6e';
const red = '#c0402d';
const redBg = '#fdeeec';
const redBorder = '#f6d3cd';

/**
 * Global pass-through for the few states the preset can't reach. PrimeVue 4.2 only exposes a
 * column's "has a filter applied" state through pass-through context, so name it with a class for
 * tally.css to draw the kit's filtered header (navy funnel on the tint).
 */
export const TallyPassThrough = {
    datatable: {
        column: {
            pcColumnFilterButton: ({ context }: { context?: { active?: boolean } }) => ({
                class: { 'p-datatable-column-filter-button-active': !!context?.active }
            })
        }
    }
};

export const TallyPreset = definePreset(Aura, {
    semantic: {
        // Navy, from the tint (50) through the brand navy (500) to near-black. Tailwind's
        // primary-* utilities read these too, so text-primary-500 links come out navy.
        primary: {
            50: tint,
            100: '#d6e4ec',
            200: '#adc8d8',
            300: '#7ba4bd',
            400: accent,
            500: navy,
            600: '#072d42',
            700: '#062637',
            800: '#041c29',
            900: '#03131c',
            950: '#020b11'
        },
        focusRing: {
            width: '2px',
            style: 'solid',
            color: accent,
            offset: '1px',
            shadow: 'none'
        },
        formField: {
            paddingX: '12px',
            paddingY: '8px',
            sm: { fontSize: '12.5px', paddingX: '10px', paddingY: '6px' },
            // "large" buttons and inputs are everywhere in the old pages; the kit has one size.
            lg: { fontSize: '13px', paddingX: '14px', paddingY: '8px' },
            borderRadius: '8px'
        },
        list: {
            padding: '6px',
            option: { padding: '8px 10px', borderRadius: '6px' }
        },
        navigation: {
            list: { padding: '6px' },
            item: { padding: '8px 10px', borderRadius: '6px' }
        },
        overlay: {
            select: { borderRadius: '10px', shadow: '0 10px 28px rgba(20, 48, 74, 0.12)' },
            popover: { borderRadius: '10px', padding: '14px', shadow: '0 10px 28px rgba(20, 48, 74, 0.12)' },
            modal: { borderRadius: '10px', padding: '20px', shadow: '0 10px 28px rgba(20, 48, 74, 0.12)' },
            navigation: { shadow: '0 10px 28px rgba(20, 48, 74, 0.12)' }
        },
        colorScheme: {
            light: {
                primary: {
                    color: navy,
                    contrastColor: '#ffffff',
                    hoverColor: navyHover,
                    activeColor: navyHover
                },
                highlight: {
                    background: tint,
                    focusBackground: '#e2ecf0',
                    color: ink,
                    focusColor: ink
                },
                mask: {
                    background: 'rgba(20, 48, 74, 0.28)',
                    color: '#ffffff'
                },
                formField: {
                    borderColor: inputBorder,
                    hoverBorderColor: '#b9c7d0',
                    focusBorderColor: accent,
                    color: ink,
                    placeholderColor: muted,
                    iconColor: muted,
                    floatLabelColor: muted,
                    floatLabelActiveColor: muted,
                    shadow: 'none'
                },
                text: {
                    color: nav,
                    hoverColor: ink,
                    mutedColor: muted,
                    hoverMutedColor: body
                },
                content: {
                    hoverBackground: tile,
                    borderColor: hairline,
                    color: nav,
                    hoverColor: ink
                },
                overlay: {
                    select: { borderColor: hairline, color: ink },
                    popover: { borderColor: hairline, color: ink },
                    modal: { borderColor: hairline, color: nav }
                },
                list: {
                    option: {
                        focusBackground: tile,
                        color: ink,
                        focusColor: ink
                    }
                },
                navigation: {
                    item: {
                        focusBackground: tile,
                        activeBackground: tile,
                        color: ink,
                        focusColor: ink,
                        activeColor: ink
                    }
                }
            }
        }
    },
    components: {
        datatable: {
            header: { padding: '12px 0', borderWidth: '0' },
            headerCell: {
                color: ink,
                hoverColor: ink,
                hoverBackground: tile,
                selectedBackground: tint,
                selectedColor: ink,
                padding: '12px 8px 10px',
                gap: '6px'
            },
            columnTitle: { fontWeight: '600' },
            row: {
                color: nav,
                hoverBackground: tile,
                hoverColor: nav,
                selectedBackground: tint,
                selectedColor: ink
            },
            bodyCell: { padding: '10px 8px' },
            footerCell: { padding: '10px 8px' },
            sortIcon: { color: muted, hoverColor: ink, size: '12px' },
            paginatorBottom: { borderColor: hairline, borderWidth: '1px 0 0 0' },
            colorScheme: {
                light: {
                    root: { borderColor: rowRule },
                    row: { stripedBackground: tile },
                    bodyCell: { selectedBorderColor: hairline }
                }
            }
        },
        paginator: {
            root: { padding: '12px 4px', gap: '2px', color: body },
            navButton: {
                color: nav,
                hoverColor: ink,
                hoverBackground: tile,
                selectedBackground: tint,
                selectedColor: ink,
                width: '30px',
                height: '30px',
                borderRadius: '6px'
            },
            currentPageReport: { color: body }
        },
        button: {
            root: {
                gap: '7px',
                paddingX: '14px',
                paddingY: '8px',
                iconOnlyWidth: '32px',
                label: { fontWeight: '600' },
                sm: { fontSize: '12.5px', paddingX: '10px', paddingY: '6px' },
                lg: { fontSize: '13px', paddingX: '14px', paddingY: '8px' }
            },
            colorScheme: {
                light: {
                    root: {
                        primary: {
                            background: navy,
                            hoverBackground: navyHover,
                            activeBackground: navyHover,
                            borderColor: navy,
                            hoverBorderColor: navyHover,
                            activeBorderColor: navyHover,
                            color: '#ffffff',
                            hoverColor: '#ffffff',
                            activeColor: '#ffffff'
                        },
                        // Kit "secondary": white, input-coloured border, ink text.
                        secondary: {
                            background: '#ffffff',
                            hoverBackground: tile,
                            activeBackground: hairline,
                            borderColor: inputBorder,
                            hoverBorderColor: inputBorder,
                            activeBorderColor: inputBorder,
                            color: ink,
                            hoverColor: ink,
                            activeColor: ink
                        },
                        // Never a filled red button: destructive actions are a secondary button
                        // in red text with a pale red border.
                        danger: {
                            background: '#ffffff',
                            hoverBackground: redBg,
                            activeBackground: redBg,
                            borderColor: redBorder,
                            hoverBorderColor: redBorder,
                            activeBorderColor: redBorder,
                            color: red,
                            hoverColor: red,
                            activeColor: red
                        }
                    },
                    // The old pages use outlined buttons for everything that isn't primary; in the
                    // kit those are the secondary button.
                    outlined: {
                        primary: { hoverBackground: tile, activeBackground: hairline, borderColor: inputBorder, color: ink },
                        secondary: { hoverBackground: tile, activeBackground: hairline, borderColor: inputBorder, color: ink },
                        danger: { hoverBackground: redBg, activeBackground: redBg, borderColor: redBorder, color: red }
                    },
                    // Text buttons are the kit's ghost button.
                    text: {
                        primary: { hoverBackground: tile, activeBackground: hairline, color: ink },
                        secondary: { hoverBackground: tile, activeBackground: hairline, color: nav },
                        danger: { hoverBackground: redBg, activeBackground: redBg, color: red }
                    },
                    link: { color: navy, hoverColor: navyHover, activeColor: navyHover }
                }
            }
        },
        tag: {
            root: {
                fontSize: '12px',
                fontWeight: '600',
                padding: '3px 9px',
                gap: '6px',
                borderRadius: '5px'
            },
            colorScheme: {
                light: {
                    primary: { background: tint, color: info },
                    secondary: { background: tile, color: body },
                    success: { background: '#def4e8', color: '#1d7f4e' },
                    info: { background: tint, color: info },
                    warn: { background: '#fdf3df', color: '#b07a1a' },
                    danger: { background: redBg, color: red }
                }
            }
        },
        dialog: {
            header: { padding: '14px 12px 14px 20px', gap: '12px' },
            title: { fontSize: '16px', fontWeight: '600' },
            content: { padding: '18px 20px 20px' },
            footer: { padding: '12px 20px', gap: '8px' }
        },
        drawer: {
            header: { padding: '14px 12px 14px 20px' },
            title: { fontSize: '16px', fontWeight: '600' },
            content: { padding: '16px 20px 20px' },
            footer: { padding: '12px 20px' }
        },
        toggleswitch: {
            root: { width: '32px', height: '18px', gap: '2px', shadow: 'none' },
            handle: { size: '14px' },
            colorScheme: {
                light: {
                    root: { background: inputBorder, hoverBackground: '#c4d0d7' }
                }
            }
        },
        menu: {
            root: { borderColor: hairline, borderRadius: '10px', color: ink },
            item: { color: ink, focusColor: ink, focusBackground: tile }
        }
    }
});
